import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, RotateCw } from "lucide-react";
import type { GroomingBatchItemView } from "@ttc-spectree/toolbox-contract";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { AdoReadErrorCard } from "@/components/grooming/ado-read-error-card";
import { BriefStateBar, useGroomOneItem } from "@/components/grooming/brief-state-bar";
import { Chip } from "@/components/grooming/grooming-chips";
import { PointScale } from "@/components/grooming/point-scale";
import { QueueBadge } from "@/components/grooming/queue-status-badge";
import { RunnerFallback } from "@/components/grooming/runner-fallback";
import { Button } from "@/components/ui/button";
import { useCan } from "@/hooks/queries/use-toolbox-access";
import { useWorkItem } from "@/hooks/queries/use-work-items";
import { useCopyFeedback } from "@/hooks/use-copy-feedback";
import { formulatorImprovePath } from "@/lib/api/work-item-interview";
import { getAdoPatErrorCode, type GroomingBrief, type GroomingSizeDriver } from "@/lib/api/work-items";
import { errorDetailsText, groomingErrorMessage, groomingFixPath } from "@/lib/grooming/error-messages";
import { confidenceTone, confidenceWords, groomCommand } from "@/lib/grooming/plain-words";
import { useRunnerBridge } from "@/lib/grooming/runner-bridge";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { cn } from "@/lib/utils";

/**
 * The review pane of a grooming run (epic PI-SPEC-SW-E279, task
 * PI-SPEC-SW-1392-2; Claude Design "All Screens" frames 2c and 2e), also the
 * result card of One item (frame 2g).
 *
 * The full brief comes from the item read's `groomingBrief` block (one live
 * ADO read), because the batch read carries only a size summary. Posting is
 * one click, never retried automatically, and never touches Story Points.
 * Every string from the tracker or a model is rendered as React text only.
 */

/** The text "Copy brief" puts on the clipboard. */
export function briefAsText(itemId: string, title: string, brief: GroomingBrief): string {
  const body = brief.body;
  const lines = [
    `AB#${itemId} ${title}`,
    `Suggested ${String(brief.suggestedPoints)} points (could be ${String(brief.pointsLow)} to ${String(brief.pointsHigh)}), ${confidenceWords(brief.confidence).toLowerCase()}`,
    "",
    body.plainSummary,
  ];
  if (body.whatChanges.length > 0) lines.push("", "What changes:", ...body.whatChanges.map((c) => `- ${c}`));
  if (body.sizeDrivers.length > 0) {
    lines.push("", "Size drivers:", ...body.sizeDrivers.map((d) => `${d.effect === "-" ? "(-)" : "(+)"} ${d.driver}`));
  }
  if (body.questions.length > 0) lines.push("", "Questions for the team:", ...body.questions.map((q) => `- ${q}`));
  return lines.join("\n");
}

/** A button that copies text and says so for two seconds. */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const { copied, copy } = useCopyFeedback();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => {
        copy(text);
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      {copied ? "Copied" : label}
    </Button>
  );
}

function Section({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <section data-testid={testId}>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/** Size drivers: ▲ adds size or risk of growth, ▼ removes size. */
function Drivers({ drivers }: { drivers: GroomingSizeDriver[] }) {
  return (
    <ul className="flex flex-col gap-1.5" data-testid="size-drivers">
      {drivers.map((d, index) => {
        const down = d.effect === "-";
        return (
          <li key={index} className="flex items-baseline gap-2 text-sm leading-normal" data-effect={d.effect}>
            <span
              className={cn("text-[11px] font-semibold", down ? "text-green-400" : "text-amber-400")}
              aria-label={down ? "Makes it smaller" : d.effect === "uncertainty" ? "Could make it bigger" : "Makes it bigger"}
            >
              {down ? "▼" : "▲"}
            </span>
            <span>
              {d.driver}
              {d.detail ? <span className="text-muted-foreground"> — {d.detail}</span> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Bullets({ items, testId }: { items: string[]; testId?: string }) {
  return (
    <ul className="flex list-disc flex-col gap-1 pl-[18px] text-sm leading-normal" data-testid={testId}>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

/** The brief itself: scale, range, confidence, reason, what changes, size drivers, questions. */
export function BriefBody({ brief, layout = "stacked" }: { brief: GroomingBrief; layout?: "stacked" | "workspace" }) {
  const body = brief.body;
  const range =
    brief.pointsLow !== brief.pointsHigh ? ` · range ${String(brief.pointsLow)} – ${String(brief.pointsHigh)}` : "";
  const confidence = (
    <Chip tone={confidenceTone(brief.confidence)} testId="confidence-chip">
      {confidenceWords(brief.confidence)}
    </Chip>
  );

  if (layout === "workspace") {
    // Claude Design "SpecTree Grooming Desktop", Workspace detail: the size card, then two columns, then questions.
    return (
      <div className="flex flex-col gap-4" data-testid="brief-body">
        <div className="glass-card flex flex-wrap items-center gap-6 rounded-[10px] px-5 py-4">
          <div className="flex flex-col gap-2">
            <PointScale size="sm" low={brief.pointsLow} high={brief.pointsHigh} pick={brief.suggestedPoints} />
            <p className="text-[13px] text-muted-foreground" data-testid="brief-suggested">
              Claude suggests <b className="text-foreground">{brief.suggestedPoints}</b>
              {range}
            </p>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span>{confidence}</span>
            <p className="text-sm leading-normal text-muted-foreground" data-testid="brief-reason">
              {body.plainSummary}
            </p>
          </div>
        </div>
        {(body.whatChanges.length > 0 || body.sizeDrivers.length > 0) && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {body.whatChanges.length > 0 && (
              <Section title="What changes" testId="what-changes">
                <Bullets items={body.whatChanges} />
              </Section>
            )}
            {body.sizeDrivers.length > 0 && (
              <Section title="Size drivers">
                <Drivers drivers={body.sizeDrivers} />
              </Section>
            )}
          </div>
        )}
        {body.questions.length > 0 && (
          <Section title="Questions for the team" testId="team-questions">
            <Bullets items={body.questions} />
          </Section>
        )}
        <p className="text-[13px] text-muted-foreground">An AI suggestion. The team decides the size.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="brief-body">
      <div className="flex flex-wrap items-center gap-4">
        <PointScale low={brief.pointsLow} high={brief.pointsHigh} pick={brief.suggestedPoints} />
        <p className="text-sm text-muted-foreground" data-testid="brief-suggested">
          Suggested <b className="text-foreground">{brief.suggestedPoints}</b>
          {range}
        </p>
      </div>
      <div className="flex items-start gap-3">
        {confidence}
        <p className="text-sm leading-normal" data-testid="brief-reason">
          {body.plainSummary}
        </p>
      </div>
      {body.whatChanges.length > 0 && (
        <Section title="What changes" testId="what-changes">
          <Bullets items={body.whatChanges} />
        </Section>
      )}
      {body.sizeDrivers.length > 0 && (
        <Section title="Size drivers">
          <Drivers drivers={body.sizeDrivers} />
        </Section>
      )}
      {body.questions.length > 0 && (
        <Section title="Questions for the team" testId="team-questions">
          <Bullets items={body.questions} />
        </Section>
      )}
      <p className="text-[13px] text-muted-foreground">An AI suggestion. The team decides the size.</p>
    </div>
  );
}

/**
 * A failed item (Claude Design "SpecTree Grooming Desktop", Workspace failure
 * card): the plain cause, a "What to do" step, Fix it for a setup problem,
 * Copy details for the Admin (code, engine message, item, exact time and
 * versions, for whoever helps; UX-PM-07) and Retry this item (a separate
 * one-item run, as in E279). The engine's own message stays behind Copy
 * details for the Admin.
 */
export function FailedItemCard({
  project,
  batchId,
  item,
}: {
  project: string;
  batchId: string;
  item: Pick<GroomingBatchItemView, "itemId" | "errorCode" | "errorMessage"> &
    Partial<Pick<GroomingBatchItemView, "finishedAt">>;
}) {
  const { support } = useRunnerBridge();
  const message = groomingErrorMessage(item.errorCode);
  const fixPath = groomingFixPath(item.errorCode, project);
  const bridge = useToolboxBridge();
  const retry = useGroomOneItem(project, item.itemId, batchId);
  const mayGroom = useCan(project, "groom-one").allowed;

  return (
    <div className="glass-card flex flex-col gap-2.5 rounded-[10px] border-primary/30 px-5 py-[18px]" data-testid="failed-item">
      <p className="flex items-center gap-2 font-semibold">
        <span
          className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary/20 text-[11px] font-bold text-red-300"
          aria-hidden="true"
        >
          ×
        </span>
        Couldn&apos;t finish this brief
      </p>
      <p className="text-sm leading-relaxed text-muted-foreground" data-testid="failed-cause">
        {message.cause}
      </p>
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-[13px] font-semibold text-zinc-200">What to do</h3>
        <p className="text-sm leading-relaxed" data-testid="failed-what-to-do">
          {message.whatToDo}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {fixPath && (
          <Link to={fixPath} className="text-sm text-link hover:underline" data-testid="failed-fix-it">
            Fix it
          </Link>
        )}
        <CopyButton
          label="Copy details for the Admin"
          text={errorDetailsText({
            project,
            itemId: item.itemId,
            batchId,
            errorCode: item.errorCode,
            errorMessage: item.errorMessage,
            at: item.finishedAt ?? null,
            appVersion: bridge?.app.version,
            contracts: bridge?.app.contracts,
          })}
        />
        <span className="flex-1" />
        {mayGroom && retry.queue.active && <QueueBadge status={retry.queue.active} />}
        {mayGroom && !retry.queue.active && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!retry.start || retry.state.status === "running"}
            onClick={() => retry.start?.()}
          >
            <RotateCw aria-hidden="true" />
            {retry.state.status === "running" ? "Retrying…" : "Retry this item"}
          </Button>
        )}
      </div>
      {retry.state.status === "done" && (
        <p className="text-sm text-green-400" role="status">
          Briefed. Open the item from the backlog or One item to review it.
        </p>
      )}
      {retry.state.status === "failed" && (
        <p className="text-sm text-destructive" role="alert">
          {retry.state.message}
        </p>
      )}
      {!retry.start && (
        <RunnerFallback
          support={support}
          command={{
            label: "Or retry this one item in Claude Code",
            command: groomCommand(item.itemId),
            helperText: "Run this in Claude Code inside the code repository. The brief is saved in SpecTree.",
          }}
        />
      )}
    </div>
  );
}

/** An item the run found below the grooming bar. */
export function NotReadyCard({ project, itemId, why }: { project: string; itemId: string; why: string | null }) {
  return (
    <div className="space-y-3 rounded-[10px] border border-amber-500/30 bg-amber-500/[0.06] p-4" data-testid="not-ready-item">
      <p className="font-semibold text-amber-300">Not ready to groom</p>
      <p className="text-sm">This item does not have what the team needs to size it yet, so Claude did not brief it.</p>
      {why && <p className="text-sm text-muted-foreground">Missing: {why}</p>}
      <Link
        to={formulatorImprovePath(project, itemId)}
        className="text-sm text-link hover:underline"
      >
        Fix in ADO Formulator
      </Link>
    </div>
  );
}

/*
 * POST_FORBIDDEN_TEXT and postErrorText live with the post rule in
 * brief-state-bar.tsx (E289); re-exported here for Post all and the gate-copy test.
 */
export { POST_FORBIDDEN_TEXT, StaleConfirmDialog, isPostable, postErrorText, useGroomOneItem } from "@/components/grooming/brief-state-bar";

export interface BriefReviewProps {
  project: string;
  itemId: string;
  /** The run the item belongs to: the run's session date, and the run-brief post rule (`manage-agenda`). */
  batchId?: string;
  /** Show "Copy brief" (One item). */
  allowCopy?: boolean;
  /** The Workspace's layout: "Claude suggests", the size card and two columns. */
  layout?: "stacked" | "workspace";
  /** Enter posts the brief when nothing else has focus (the Workspace, with `manage-agenda`). */
  enterToPost?: boolean;
}

/**
 * The review pane for one briefed item (Workspace detail), or One item's
 * result card. Its footer is the shared BriefStateBar (E289): the same chip,
 * preparation line and Hold back / Post to ADO as the reading pane.
 */
export function BriefReview({ project, itemId, batchId, allowCopy = false, layout = "stacked", enterToPost = false }: BriefReviewProps) {
  const itemQuery = useWorkItem("ado", project, itemId);
  const item = itemQuery.data;
  const block = item?.groomingBrief;
  const brief = block?.brief ?? null;
  const patError = getAdoPatErrorCode(itemQuery.error);

  if (patError) return <AdoPatCallout code={patError} />;
  if (itemQuery.isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center" role="status" aria-live="polite">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="sr-only">Reading the brief…</span>
      </div>
    );
  }
  if (itemQuery.isError || !item) {
    // B6 (UX-PM-37, PI-SPEC-SW-1480): the cause, Try again and what was already tried.
    return <AdoReadErrorCard query={itemQuery} subject="item" project={project} itemId={itemId} compact testId="item-read-error" />;
  }

  const workspace = layout === "workspace";
  return (
    <div className={cn("flex flex-col", workspace ? "gap-4" : "gap-5")} data-testid="brief-review">
      <div className="space-y-1.5">
        <p className="font-mono text-xs text-muted-foreground" data-testid="review-id-line">
          AB#{itemId} · {item.typeName} ·{" "}
          <a href={item.ref.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-sans text-link hover:underline">
            Open in ADO
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </p>
        <h2 className={cn("font-display font-semibold leading-tight tracking-[-0.01em]", workspace ? "text-[22px]" : "text-[20px]")}>
          {item.title}
        </h2>
      </div>

      {!brief ? (
        <p className="text-sm text-muted-foreground" data-testid="no-brief">
          This item has no brief yet.
        </p>
      ) : (
        <>
          {block?.stale && (
            <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300" role="status" data-testid="stale-note">
              The item changed in ADO since this brief was written.
            </p>
          )}
          <BriefBody brief={brief} layout={layout} />
          <BriefStateBar
            project={project}
            itemId={itemId}
            batchId={batchId}
            variant="review"
            enterToPost={enterToPost}
            extra={allowCopy ? <CopyButton label="Copy brief" text={briefAsText(itemId, item.title, brief)} /> : undefined}
          />
        </>
      )}
    </div>
  );
}
